import React, { useRef, useState } from 'react';
import { Upload, X, Loader2, FileDown, Image } from 'lucide-react';
import { uploadWebsiteBlockImage } from '../../../../services/storageService';
import { uploadDownloadFile, deleteDownloadFile } from '../../../../services/storageService';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

const FILE_TYPE_COLORS = {
  pdf: 'bg-red-500/20 text-red-400',
  pgn: 'bg-green-500/20 text-green-400',
  epub: 'bg-blue-500/20 text-blue-400',
  zip: 'bg-yellow-500/20 text-yellow-400',
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * DownloadsEditor - Editor for the featured download block
 */
const DownloadsEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const { content } = block;
  const coverInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  // Cover image upload (reuses website block image upload)
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingCover(true);
    setUploadError('');

    try {
      const downloadURL = await uploadWebsiteBlockImage(file, profileId, block.id);
      handleChange('coverImage', downloadURL);
    } catch (error) {
      setUploadError(error.message || 'Failed to upload cover image');
    } finally {
      setIsUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  // Downloadable file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingFile(true);
    setUploadError('');

    try {
      const result = await uploadDownloadFile(file, profileId, block.id);
      onChange({
        fileUrl: result.url,
        fileName: result.fileName,
        fileSize: result.fileSize,
        fileType: result.fileType,
        // Auto-set title from filename if empty
        ...(content.title ? {} : { title: result.fileName.replace(/\.[^/.]+$/, '') }),
      });
    } catch (error) {
      setUploadError(error.message || 'Failed to upload file');
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveCover = () => {
    handleChange('coverImage', '');
  };

  const handleRemoveFile = async () => {
    if (content.fileUrl) {
      await deleteDownloadFile(content.fileUrl);
    }
    onChange({ fileUrl: '', fileName: '', fileSize: 0, fileType: '' });
  };

  const badgeClass = FILE_TYPE_COLORS[content.fileType?.toLowerCase()] || 'bg-gray-500/20 text-gray-400';

  return (
    <div className="space-y-4">
      {/* Cover Image Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Cover Image
        </label>
        {content.coverImage ? (
          <div className="relative group">
            <img
              src={content.coverImage}
              alt="Cover preview"
              className="w-full h-40 object-cover rounded-lg border border-gray-700"
            />
            <button
              onClick={handleRemoveCover}
              className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-red-600 rounded-full text-white transition-colors opacity-0 group-hover:opacity-100"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => coverInputRef.current?.click()}
            disabled={isUploadingCover}
            className="w-full h-32 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-700 rounded-lg hover:border-[#749555] transition-colors"
          >
            {isUploadingCover ? (
              <Loader2 size={24} className="text-[#749555] animate-spin" />
            ) : (
              <>
                <Image size={24} className="text-gray-500" />
                <span className="text-sm text-gray-400">Upload cover image</span>
              </>
            )}
          </button>
        )}
        <input
          ref={coverInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleCoverUpload}
          className="hidden"
        />
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Downloadable File
        </label>
        {content.fileUrl ? (
          <div className="flex items-center gap-3 p-3 bg-[#749555]/10 border border-[#749555]/30 rounded-lg">
            <div className="p-2 bg-[#1a1a2e] rounded-lg flex-shrink-0">
              <FileDown size={20} className="text-[#749555]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white font-medium truncate">{content.fileName}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${badgeClass}`}>
                  {content.fileType}
                </span>
                <span className="text-xs text-gray-500">{formatFileSize(content.fileSize)}</span>
              </div>
            </div>
            <button
              onClick={handleRemoveFile}
              className="p-1.5 text-gray-400 hover:text-red-400 transition-colors flex-shrink-0"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploadingFile}
            className="w-full h-20 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-700 rounded-lg hover:border-[#749555] transition-colors"
          >
            {isUploadingFile ? (
              <Loader2 size={24} className="text-[#749555] animate-spin" />
            ) : (
              <>
                <Upload size={20} className="text-gray-500" />
                <span className="text-xs text-gray-400">Upload file (PGN, PDF, EPUB, ZIP, etc. — max 25MB)</span>
              </>
            )}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>

      {/* Upload Error */}
      {uploadError && (
        <p className="text-red-400 text-sm">{uploadError}</p>
      )}

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Title
        </label>
        <input
          type="text"
          value={content.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="My Chess Opening Repertoire"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Description
        </label>
        <textarea
          value={content.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="A curated collection of opening lines for club-level players..."
          rows={3}
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none resize-none"
        />
      </div>

      {/* Button Text */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Button Text
        </label>
        <input
          type="text"
          value={content.buttonText || 'Download'}
          onChange={(e) => handleChange('buttonText', e.target.value)}
          placeholder="Download"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Require Email Toggle */}
      <div className="flex items-center justify-between bg-[#1a1a2e] border border-gray-700 rounded-lg p-3">
        <div>
          <p className="text-sm font-medium text-white">Require Email</p>
          <p className="text-xs text-gray-500">Visitors enter email before downloading</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={content.requireEmail || false}
            onChange={(e) => handleChange('requireEmail', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#749555]"></div>
        </label>
      </div>

      {/* Layout */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Layout
        </label>
        <div className="flex gap-2">
          {[
            { value: 'imageLeft', label: 'Image Left' },
            { value: 'imageRight', label: 'Image Right' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleChange('layout', option.value)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                content.layout === option.value
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (<>
      <SizeSpacingPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['downloads']}
      />
      <ColorsFontsPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['downloads']}
        theme={theme || {}}
        blockType="downloads"
      />
      </>)}
    </div>
  );
};

export default DownloadsEditor;
